import { useEffect, useRef } from "react";
import { CosmicScene } from "../three/CosmicScene";
import { ZODIAC_PIN_VH, TAROT_PIN_VH } from "../zodiacConfig";

const SECTION_STATES = [
  { id: "hero", state: { x: 0, y: 0.4, z: 0, scale: 2.8, lightAngle: 0, brightness: 1 } },
  { id: "intro", state: { x: 4.6, y: 2.8, z: -3, scale: 0.85, lightAngle: Math.PI / 2, brightness: 1 } },
  { id: "zodiac", state: { x: -5.85, y: 0.05, z: -2.5, scale: 0.95, lightAngle: Math.PI / 4, brightness: 0.62 } },
  { id: "tarot", state: { x: -5.6, y: -1.2, z: -2, scale: 0.65, lightAngle: Math.PI * 0.82, brightness: 1 } },
  { id: "benefits", state: { x: 5.4, y: 2.6, z: -4.5, scale: 0.42, lightAngle: Math.PI * 0.3, brightness: 1 } },
  { id: "services", state: { x: -5.5, y: -2.4, z: -4.5, scale: 0.4, lightAngle: Math.PI * 0.5, brightness: 1 } },
  { id: "stats", state: { x: 5.2, y: 2.3, z: -4.8, scale: 0.34, lightAngle: Math.PI * 0.65, brightness: 1 } },
  { id: "testimonials", state: { x: -5.6, y: -2.2, z: -4.8, scale: 0.36, lightAngle: Math.PI * 0.85, brightness: 1 } },
  { id: "pricing", state: { x: 5.5, y: 2.5, z: -4.6, scale: 0.38, lightAngle: Math.PI * 1.05, brightness: 1 } },
  { id: "cta", state: { x: 0, y: -0.35, z: 0, scale: 2.9, lightAngle: 0, brightness: 0.7 } },
];

const PINNED_VH = { zodiac: ZODIAC_PIN_VH, tarot: TAROT_PIN_VH };

const getFraction = (id, maxScroll) => {
  const el = document.getElementById(id);
  if (!el || maxScroll <= 0) return 0;
  const top = el.getBoundingClientRect().top + window.scrollY;
  return Math.min(Math.max(top / maxScroll, 0), 1);
};

export const CosmicBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const scene = new CosmicScene(canvasRef.current);
    const mobileScale = window.innerWidth < 768 ? 0.6 : 1;
    const scaled = (state) => ({ ...state, scale: state.scale * mobileScale });

    Object.assign(scene.moonState, scaled(SECTION_STATES[0].state));

    let rafId;
    const loop = () => {
      scene.setScrollOffset(window.scrollY);
      scene.render();
      rafId = requestAnimationFrame(loop);
    };
    loop();

    let scrollTriggerInstance = null;
    let timeline = null;

    const buildTimeline = () => {
      if (!window.gsap || !window.ScrollTrigger) return;
      const gsapLib = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;

      if (scrollTriggerInstance) scrollTriggerInstance.kill();
      if (timeline) timeline.kill();

      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

      const keyframes = [];
      let prevAt = -1;
      SECTION_STATES.forEach(({ id, state }) => {
        const frac = getFraction(id, maxScroll);
        const at = Math.max(frac, prevAt + 0.001);
        keyframes.push({ at, state: scaled(state) });
        prevAt = at;

        const pinVh = PINNED_VH[id];
        if (pinVh) {
          const holdAt = Math.max(at + (maxScroll > 0 ? (window.innerHeight * pinVh) / maxScroll : 0.01), at + 0.01);
          keyframes.push({ at: holdAt, state: scaled(state) });
          prevAt = holdAt;
        }
      });

      timeline = gsapLib.timeline();
      for (let i = 1; i < keyframes.length; i++) {
        const prev = keyframes[i - 1];
        const curr = keyframes[i];
        const duration = Math.max(curr.at - prev.at, 0.001);
        timeline.to(scene.moonState, { ...curr.state, duration, ease: "none" }, prev.at);
      }

      scrollTriggerInstance = ScrollTrigger.create({
        trigger: document.documentElement,
        start: "top top",
        end: "bottom bottom",
        scrub: true,
        animation: timeline,
      });
    };

    const initTimer = setTimeout(buildTimeline, 500);

    let resizeTimer;
    const handleResize = () => {
      scene.onResize();
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(buildTimeline, 250);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(initTimer);
      clearTimeout(resizeTimer);
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
      if (scrollTriggerInstance) scrollTriggerInstance.kill();
      if (timeline) timeline.kill();
      scene.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="cosmic-canvas"
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
    />
  );
};
